import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { acceptInvite } from '../api/services/userService/User';
import { 
  Container, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  CircularProgress, 
  Alert,
  Box
} from '@mui/material';
import { styled } from '@mui/material/styles';

const FormContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: 600,
  margin: '40px auto',
  boxShadow: theme.shadows[3]
}));

const RegistrationInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const isMountedRef = useRef(true);
  const timeoutRef = useRef(null);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    company: '',
    position: ''
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isRedirecting, setIsRedirecting] = useState(false);
  const navigationRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    navigationRef.current = false;
    
    return () => {
      isMountedRef.current = false;
      navigationRef.current = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (isRedirecting && !navigationRef.current) {
      timeoutRef.current = setTimeout(() => {
        if (isMountedRef.current && !navigationRef.current) {
          navigationRef.current = true;
          isMountedRef.current = false;
          navigate('/', { replace: true });
        }
      }, 1500);
      
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      };
    }
  }, [isRedirecting, navigate]);

  const validateForm = () => {
    if (navigationRef.current) return false;
    
    const newErrors = {};
    
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.email.includes('@')) newErrors.email = 'Valid email is required';
    if (!formData.password) newErrors.password = 'Password is required';
    if (formData.password && formData.password.length < 8) 
      newErrors.password = 'Password should be at least 8 characters';
    if (formData.password !== formData.confirmPassword) 
      newErrors.confirmPassword = 'Passwords do not match';
    
    if (isMountedRef.current && !navigationRef.current) {
      setErrors(newErrors);
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (!isMountedRef.current || navigationRef.current) return;
    
    setFormData({
      ...formData,
      [name]: value
    });
    
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm() || !isMountedRef.current || navigationRef.current) return;
    
    if (isMountedRef.current && !navigationRef.current) {
      setLoading(true);
      setMessage({ type: '', text: '' });
    }
    
    try {
      const userData = {
        ...formData,
        token
      };
      
      const response = await acceptInvite(userData);
      
      if (!isMountedRef.current || navigationRef.current) return;
      
      if (response.success) {
        if (isMountedRef.current && !navigationRef.current) {
          setIsRedirecting(true);
          setMessage({ 
            type: 'success', 
            text: 'Registration successful! Redirecting...' 
          });
        }
      } else {
        if (isMountedRef.current && !navigationRef.current) {
          setMessage({ 
            type: 'error', 
            text: 'Registration Link expired, please contact the Admin' 
          });
          setLoading(false);
        }
      }
    } catch (error) {
      if (isMountedRef.current && !navigationRef.current) {
        setMessage({ 
          type: 'error', 
          text: 'Registration Link expired, please contact the Admin' 
        });
        setLoading(false);
      }
      console.error('Registration error:', error);
    }
  };

  const handleLogout = () => {
    keycloak.logout();
  };

  if (keycloak.authenticated) {
    return (
      <Container maxWidth="md">
        <FormContainer>
          <Typography variant="h4" gutterBottom align="center">
            Already Logged In
          </Typography>
          <Typography variant="body1" gutterBottom align="center" color="textSecondary">
            You are currently logged in. To complete this registration invite, you may need to log out first.
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 4 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleLogout}
              sx={{ minWidth: 150 }}
            >
              Log Out
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate('/home')}
              sx={{ minWidth: 150 }}
            >
              Go to Dashboard
            </Button>
          </Box>
        </FormContainer>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <FormContainer>
        <Typography variant="h4" gutterBottom align="center">
          Complete Your Registration
        </Typography>
        <Typography variant="body1" gutterBottom align="center" color="textSecondary">
          You've been invited to join the platform. Please complete your registration.
        </Typography>

        <Box sx={{ width: '100%', mt: 4 }}>
          {message.text && !navigationRef.current && (
            <Alert key={`${message.type}-${message.text}`} severity={message.type} sx={{ mb: 3 }}>
              {message.text}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  error={!!errors.firstName}
                  helperText={errors.firstName || ''}
                  required
                  margin="normal"
                  disabled={isRedirecting || loading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  error={!!errors.lastName}
                  helperText={errors.lastName || ''}
                  required
                  margin="normal"
                  disabled={isRedirecting || loading}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  error={!!errors.email}
                  helperText={errors.email || ''}
                  required
                  margin="normal"
                  disabled={isRedirecting || loading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  error={!!errors.password}
                  helperText={errors.password || ''}
                  required
                  margin="normal"
                  disabled={isRedirecting || loading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword || ''}
                  required
                  margin="normal"
                  disabled={isRedirecting || loading}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  margin="normal"
                  disabled={isRedirecting || loading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  margin="normal"
                  disabled={isRedirecting || loading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Position"
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                  margin="normal"
                  disabled={isRedirecting || loading}
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading || isRedirecting}
                sx={{ minWidth: 150 }}
              >
                {loading || isRedirecting ? <CircularProgress size={24} /> : 'Complete Registration'}
              </Button>
            </Box>
          </form>
        </Box>
      </FormContainer>
    </Container>
  );
};

export default RegistrationInvite;
